import React, { useState, useEffect, useCallback } from 'react';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiSearch,
  HiUser,
  HiMail,
  HiPhone,
  HiShieldCheck,
  HiCheckCircle,
  HiXCircle,
} from 'react-icons/hi';
import Select from './Select';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import PhoneInput from './PhoneInput';
import { PERMISSAO_MAP } from '../types';
import { apiConfig } from '../config/environment';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [permissoes, setPermissoes] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    role: 'funcionario',
    ativo: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const { notifications, removeNotification, showSuccess, showWarning } =
    useNotification();

  // Carregar permissões do endpoint
  const loadPermissoes = async () => {
    try {
      const data = await apiService.getPermissaos();
      const permissoesList = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setPermissoes(permissoesList);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setPermissoes([]);
    }
  };

  // Criar roleOptions a partir das permissões carregadas
  const roleOptions = permissoes.length > 0
    ? permissoes.map(perm => ({
        value: perm.id?.toString() || '',
        label: perm.permissao || perm.nome || perm.name || perm.permission || `Permissão ${perm.id}`,
      }))
    : [
        { value: 'admin', label: 'Administrador' },
        { value: 'gerente', label: 'Gerente' },
        { value: 'funcionario', label: 'Funcionário' },
        { value: 'transportador', label: 'Transportador' },
        { value: 'entregador', label: 'Entregador' },
      ];

  // Função para obter o nome da permissão pelo permissaos_id
  const getPermissaoNome = (permissaosId) => {
    if (!permissaosId) return 'Funcionário';
    const id = typeof permissaosId === 'string' ? parseInt(permissaosId) : permissaosId;

    // Buscar nas permissões carregadas
    const permissao = permissoes.find(p => p.id === id);
    if (permissao) {
      return permissao.permissao || permissao.nome || permissao.name || permissao.permission || 'Funcionário';
    }

    // Fallback para PERMISSAO_MAP
    return PERMISSAO_MAP[id] || 'Funcionário';
  };

  // Função para obter o permissaos_id pelo role (para o formulário)
  const getPermissaoIdFromRole = (role) => {
    // Se roleOptions já está usando IDs, retornar o ID diretamente
    if (permissoes.length > 0 && role) {
      const permissao = permissoes.find(p => p.id?.toString() === role?.toString());
      if (permissao) return permissao.id;
    }

    // Fallback para mapeamento antigo
    const roleToPermissaoId = {
      admin: 1,
      gerente: 2,
      funcionario: 3,
      transportador: 4,
      entregador: 5,
    };
    return roleToPermissaoId[role] || 3;
  };

  // Função para obter o role pelo permissaos_id (para o formulário)
  const getRoleFromPermissaoId = (permissaosId) => {
    if (!permissaosId) return permissoes.length > 0 ? permissoes[0]?.id?.toString() : 'funcionario';
    const id = typeof permissaosId === 'string' ? parseInt(permissaosId) : permissaosId;

    // Se temos permissões carregadas, usar o ID diretamente
    if (permissoes.length > 0) {
      return id.toString();
    }

    // Fallback para mapeamento antigo
    const permissaoIdToRole = {
      1: 'admin',
      2: 'gerente',
      3: 'funcionario',
      4: 'transportador',
      5: 'entregador',
    };
    return permissaoIdToRole[id] || 'funcionario';
  };

  useEffect(() => {
    loadPermissoes();
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resetar para primeira página quando o termo de busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Função para construir URL completa do avatar
  const getAvatarUrl = useCallback((avatarPath) => {
    if (!avatarPath || !avatarPath.trim()) return '';

    // Se já é uma URL completa (começa com http), retorna como está
    if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
      return avatarPath;
    }

    // Normalizar o caminho: substituir barras invertidas por barras normais e remover espaços
    const normalizedPath = avatarPath.replace(/\\/g, '/').trim();

    // Remove barras duplicadas e constrói a URL completa
    const baseUrl = apiConfig.baseURL.replace(/\/$/, ''); // Remove barra final se existir
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
    return `${baseUrl}${cleanPath}`;
  }, []);

  // Função para obter iniciais do nome
  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'U';
    const nameStr = name || '';
    const names = nameStr.trim().split(' ').filter(n => n.length > 0);
    if (names.length === 0) return 'U';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return 'Nunca';
    }
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Carregar todos os usuários (a filtragem será feita localmente)
      const data = await apiService.getUsers({
        page: 1,
        per_page: 1000, // Carregar muitos para filtrar localmente
      });
      const usersList = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      setUsers(usersList);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
      showWarning('Erro ao carregar usuários. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.nome.trim() || !formData.email.trim()) {
      showWarning('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showWarning('Email inválido');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await apiService.updateUser(editingId, {
          nome: formData.nome,
          email: formData.email,
          telefone: formData.telefone,
          permissaos_id: getPermissaoIdFromRole(formData.role),
          status: formData.ativo ? 'active' : 'inactive',
        });
        showSuccess('Usuário atualizado com sucesso!');
      } else {
        // Para criar usuário, precisamos de senha
        // Vamos gerar uma senha temporária ou pedir ao usuário
        const tempPassword = 'Temp@123'; // Em produção, isso deve ser gerado ou pedido ao usuário
        await apiService.createUser({
          nome: formData.nome,
          email: formData.email,
          password: tempPassword,
          telefone: formData.telefone || undefined,
          permissaos_id: getPermissaoIdFromRole(formData.role),
          status: formData.ativo ? 'active' : 'inactive',
        });
        showSuccess('Usuário criado com sucesso!');
      }
      handleCloseModal();
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      showWarning(error.message || 'Erro ao salvar usuário. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = user => {
    // Obter permissaos_id do usuário (pode vir de diferentes lugares)
    const permissaosId = user.permissaos_id || user.permissao?.id || user.permissaos_id;

    setFormData({
      nome: user.name || user.nome || '',
      email: user.email || '',
      telefone: user.phone || user.telefone || '',
      role: getRoleFromPermissaoId(permissaosId),
      ativo: user.status === 1 || user.ativo || false,
    });
    setEditingId(user.id);
    setShowModal(true);
  };

  const handleDelete = id => {
    setUserToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteUser(userToDelete);
      setShowConfirmDialog(false);
      setUserToDelete(null);
      showSuccess('Usuário excluído com sucesso!');
      loadUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      showWarning(error.message || 'Erro ao excluir usuário. Tente novamente.');
      setShowConfirmDialog(false);
      setUserToDelete(null);
    }
  };

  const handleActivateUser = async id => {
    try {
      await apiService.updateUser(id, {
        status: 'active',
      });
      showSuccess('Usuário ativado com sucesso!');
      loadUsers();
    } catch (error) {
      console.error('Erro ao ativar usuário:', error);
      showWarning(error.message || 'Erro ao ativar usuário. Tente novamente.');
    }
  };

  const handleDeactivateUser = async id => {
    try {
      await apiService.updateUser(id, {
        status: 'inactive',
      });
      showSuccess('Usuário inativado com sucesso!');
      loadUsers();
    } catch (error) {
      console.error('Erro ao inativar usuário:', error);
      showWarning(error.message || 'Erro ao inativar usuário. Tente novamente.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      role: 'funcionario',
      ativo: true,
    });
    setEditingId(null);
    setSaving(false);
  };

  const getRoleLabel = (permissaosId) => {
    return getPermissaoNome(permissaosId);
  };

  const getRoleBadgeClass = (permissaosId) => {
    if (!permissaosId) return 'badge-info';
    const id = typeof permissaosId === 'string' ? parseInt(permissaosId) : permissaosId;

    // Mapeamento de cores baseado no ID (pode ser ajustado conforme necessário)
    const badgeClasses = {
      1: 'badge-danger',      // Admin
      2: 'badge-warning',      // Gerente
      3: 'badge-info',         // Funcionário
      4: 'badge-primary',      // Transportador
      5: 'badge-secondary',    // Entregador
    };

    return badgeClasses[id] || 'badge-secondary';
  };

  // Filtros e paginação
  // Se a API não filtrar, aplicamos filtro local também
  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const nome = (user.name || user.nome || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const telefone = (user.phone || user.telefone || '').toLowerCase();
    const permissaoNome = getPermissaoNome(user.permissaos_id || user.permissao?.id).toLowerCase();

    return nome.includes(search) ||
           email.includes(search) ||
           telefone.includes(search) ||
           permissaoNome.includes(search);
  }) : [];
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className='page-container'>
      <div className='breadcrumb'>
        <span>Painel</span>
        <span>Usuários</span>
      </div>

      <div className='page-header'>
        <div>
          <h1>Usuários</h1>
          <p>Gerencie os usuários do sistema</p>
        </div>
        <button className='btn btn-primary' onClick={() => setShowModal(true)}>
          <HiPlus size={20} />
          Novo Usuário
        </button>
      </div>

      <div className='page-actions'>
        <div className='search-box'>
          <HiSearch className='search-icon' size={20} />
          <input
            type='text'
            placeholder='Buscar por nome, email, telefone ou permissão...'
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className='card'>
        {loading ? (
          <SkeletonTable rows={8} columns={6} />
        ) : paginatedUsers.length === 0 ? (
          <div className='empty-state'>
            <p>
              {searchTerm
                ? 'Nenhum usuário encontrado'
                : 'Nenhum usuário cadastrado'}
            </p>
          </div>
        ) : (
          <>
            <div className='table-container'>
              <table className='data-table'>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>Permissão</th>
                    <th>Status</th>
                    <th>Último Login</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, position: 'relative', background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                            {user.avatar ? (
                              <img
                                src={getAvatarUrl(user.avatar)}
                                alt={user.name || user.nome}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const placeholder = e.target.nextElementSibling;
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                              />
                            ) : null}
                            <div
                              className="avatar-placeholder"
                              style={{
                                display: user.avatar ? 'none' : 'flex',
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '0.875rem'
                              }}
                            >
                              {getInitials(user.name || user.nome)}
                            </div>
                          </div>
                          <div>
                            <strong>{user.name || user.nome}</strong>
                            <br />
                            <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>
                              {user.email}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>{user.phone || user.telefone || '-'}</td>
                      <td>
                        <span
                          className={`badge ${getRoleBadgeClass(
                            user.permissaos_id || user.permissao?.id
                          )}`}
                        >
                          {getRoleLabel(
                            user.permissaos_id || user.permissao?.id
                          )}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            user.status === 1 || user.ativo
                              ? 'badge-success'
                              : 'badge-danger'
                          }`}
                        >
                          {user.status === 1 || user.ativo
                            ? 'Ativo'
                            : 'Inativo'}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                          {user.lastLogin || user.last_login
                            ? formatDate(user.lastLogin || user.last_login)
                            : 'Nunca'}
                        </span>
                      </td>
                      <td>
                        <div className='actions'>
                          <button
                            className='btn-icon btn-edit'
                            onClick={() => handleEdit(user)}
                            title='Editar'
                          >
                            <HiPencil size={18} />
                          </button>
                          {user.status === 1 || user.ativo ? (
                            <button
                              className='btn-icon btn-warning'
                              onClick={() => handleDeactivateUser(user.id)}
                              title='Inativar usuário'
                            >
                              <HiXCircle size={18} />
                            </button>
                          ) : (
                            <button
                              className='btn-icon btn-success'
                              onClick={() => handleActivateUser(user.id)}
                              title='Ativar usuário'
                            >
                              <HiCheckCircle size={18} />
                            </button>
                          )}
                          <button
                            className='btn-icon btn-delete'
                            onClick={() => handleDelete(user.id)}
                            title='Excluir'
                          >
                            <HiTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='table-footer'>
              <div className='pagination-info'>
                {filteredUsers.length > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, filteredUsers.length)} de {filteredUsers.length} registro{filteredUsers.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className='items-per-page'>
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className='select-sm'
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ]}
                />
              </div>
              <div className='pagination'>
                <button
                  className='pagination-btn'
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className='pagination-info'>
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className='pagination-btn'
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className='modal-overlay'>
          <div className='modal-content' onClick={e => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>{editingId ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button className='modal-close' onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className='modal-form'>
              <div className='form-group'>
                <label>
                  <HiUser size={18} />
                  Nome Completo *
                </label>
                <input
                  type='text'
                  value={formData.nome}
                  onChange={e =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                  required
                  placeholder='Digite o nome completo'
                />
              </div>

              <div className='form-group'>
                <label>
                  <HiMail size={18} />
                  Email *
                </label>
                <input
                  type='email'
                  value={formData.email}
                  onChange={e =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  placeholder='usuario@exemplo.com'
                />
              </div>

              <div className='form-group'>
                <label>
                  <HiPhone size={18} />
                  Telefone
                </label>
                <PhoneInput
                  value={formData.telefone}
                  onChange={e =>
                    setFormData({ ...formData, telefone: e.target.value })
                  }
                  placeholder='Número do telefone'
                />
              </div>

              <div className='form-group'>
                <label>
                  <HiShieldCheck size={18} />
                  Tipo de Permissão *
                </label>
                <Select
                  value={formData.role}
                  onChange={e =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  required
                  options={roleOptions}
                />
                <small className='input-hint'>
                  Selecione o tipo de permissão que o usuário terá no sistema
                </small>
              </div>

              <div className='form-group'>
                <label>
                  <input
                    type='checkbox'
                    checked={formData.ativo}
                    onChange={e =>
                      setFormData({ ...formData, ativo: e.target.checked })
                    }
                  />
                  <span>Usuário ativo</span>
                </label>
                <small className='input-hint'>
                  Usuários inativos não podem acessar o sistema
                </small>
              </div>

              <div className='modal-actions'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleCloseModal}
                >
                  Cancelar
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : `${editingId ? 'Atualizar' : 'Salvar'} Usuário`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title='Confirmar Exclusão'
        message='Tem certeza que deseja excluir este usuário?'
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setUserToDelete(null);
        }}
        confirmText='Excluir'
        cancelText='Cancelar'
        type='danger'
      />

      <div className='notifications-container'>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Users;
